// ABOUTME: Chart component showing behavior patterns by time of day OR day of week for family or individual child
// ABOUTME: Displays hourly distribution or day-of-week distribution with toggle support (Issue #1132)

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipContentProps } from 'recharts';
import { Box, Typography, Dialog, DialogTitle, DialogContent, useTheme, CircularProgress, useMediaQuery, Switch } from '@mui/material';
import { BehaviorRecord } from '../types/api';
import { TimeWindowOption } from './TimeWindowSelector';
import { getTimeWindowDisplayTitle } from '../utils/dateUtils';
import { analyticsAPI } from '../services/api';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../utils/mobileOptimization';
import { BehaviorType } from '../types/api';
import { getBehaviorIcon, getBehaviorColor } from '../utils/behaviorUtils';
import { HourlyBehaviorAverage, DayOfWeekBehaviorAverage } from '../types/analytics';
import naluV1Theme from '../theme/theme.nalu-v1';

// Day of week constants (Issue #1132)
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface TimePatternsChartProps {
  behaviorRecords?: BehaviorRecord[];
  selectedPeriod?: 'week' | 'month'; // Deprecated - use timeWindow instead
  timeWindow?: string | TimeWindowOption; // Unified time window selection
  familyId?: number;
  childId?: number;
  childName?: string;
  familyTimezone?: string;
  testId?: string;
  // Pre-processed API data props (Issue #1135)
  hourlyAveragesData?: HourlyBehaviorAverage[];
  dayOfWeekAveragesData?: DayOfWeekBehaviorAverage[];
}

interface HourlyData {
  hour: string;
  hourNum: number;
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins: number;
  makeItRights: number;
  oneOnOneTimes: number;
  total: number;
}

interface DetailedHourData {
  hour: string;
  behaviors: BehaviorRecord[];
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins: number;
  makeItRights: number;
  oneOnOneTimes: number;
}

interface DayOfWeekData {
  day: string;
  dayNum: number;
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins: number;
  makeItRights: number;
  oneOnOneTimes: number;
  total: number;
}

interface DetailedDayData {
  day: string;
  behaviors: BehaviorRecord[];
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins: number;
  makeItRights: number;
  oneOnOneTimes: number;
}

const TimePatternsChart: React.FC<TimePatternsChartProps> = ({
  behaviorRecords = [],
  selectedPeriod,
  timeWindow,
  familyId,
  childId,
  childName,
  familyTimezone,
  testId,
  hourlyAveragesData: initialHourlyAveragesData,
  dayOfWeekAveragesData: initialDayOfWeekAveragesData
}) => {
  const [viewMode, setViewMode] = useState<'hour' | 'dayOfWeek'>('dayOfWeek'); // Toggle state - defaults to day of week (Issue #1135)
  const [selectedHour, setSelectedHour] = useState<DetailedHourData | null>(null);
  const [selectedDay, setSelectedDay] = useState<DetailedDayData | null>(null);
  const [hourlyAveragesData, setHourlyAveragesData] = useState(initialHourlyAveragesData);
  const [dayOfWeekAveragesData, setDayOfWeekAveragesData] = useState(initialDayOfWeekAveragesData);
  const [loading, setLoading] = useState(!initialDayOfWeekAveragesData && !!familyId); // Load day-of-week data first (Issue #1135)
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('rotatedTimeChart', isMobile);

  // Fetch day-of-week behavior patterns on mount (default view) - Issue #1135
  useEffect(() => {
    if (initialDayOfWeekAveragesData || !familyId) {
      setDayOfWeekAveragesData(initialDayOfWeekAveragesData);
      setLoading(false);
      return;
    }

    const fetchDayOfWeekPatterns = async () => {
      setLoading(true);
      setError(null);

      try {
        const patternsData = await analyticsAPI.getBehaviorPatternsByDayOfWeek(familyId, familyTimezone, childId);
        setDayOfWeekAveragesData(patternsData?.dayOfWeekAverages);
      } catch (error) {
        console.error('Failed to load day-of-week patterns:', error);
        setError('Failed to load behavior patterns');
        setDayOfWeekAveragesData(undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchDayOfWeekPatterns();
  }, [familyId, childId, familyTimezone, initialDayOfWeekAveragesData]);

  // Fetch hourly behavior patterns when switching to hour view (lazy load) - Issue #1135
  useEffect(() => {
    // Only fetch if switching to hour view, don't have the data, and have a familyId
    if (viewMode !== 'hour' || hourlyAveragesData || !familyId) {
      return;
    }

    const fetchHourlyPatterns = async () => {
      setLoading(true);
      setError(null);

      try {
        const patternsData = await analyticsAPI.getBehaviorPatternsByTime(familyId, familyTimezone, childId);
        setHourlyAveragesData(patternsData?.hourlyAverages);
      } catch (error) {
        console.error('Failed to load hourly patterns:', error);
        setError('Failed to load behavior patterns');
        setHourlyAveragesData(undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchHourlyPatterns();
  }, [viewMode, familyId, childId, familyTimezone, hourlyAveragesData]);

  // Helper function to get hour in family timezone
  const getHourInFamilyTimezone = (timestamp: string): number => {
    const date = new Date(timestamp);
    if (familyTimezone) {
      try {
        const timeInTimezone = new Intl.DateTimeFormat('en-US', {
          timeZone: familyTimezone,
          hour: 'numeric',
          hour12: false,
        }).format(date);
        return parseInt(timeInTimezone, 10);
      } catch {
        // Invalid timezone, fall back to UTC
      }
    }
    // Fallback to UTC if no familyTimezone or if timezone is invalid
    return date.getUTCHours();
  };

  /**
   * Converts hex color to RGB values for rgba() usage
   * @param hex - Hex color string (with or without #)
   * @returns RGB object with r, g, b values
   */
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  // Convert primary color to RGB once for heat map usage
  const primaryRgb = hexToRgb(theme.palette.primary.main);

  // Behavior type configuration - uses centralized utilities for consistency
  const BEHAVIOR_CONFIG = [
    { key: 'stars', label: 'Stars', color: getBehaviorColor(BehaviorType.Star), typeId: BehaviorType.Star },
    { key: 'triangles', label: 'Triangles', color: getBehaviorColor(BehaviorType.Triangle), typeId: BehaviorType.Triangle },
    { key: 'squares', label: 'Storms', color: getBehaviorColor(BehaviorType.Square), typeId: BehaviorType.Square },
    { key: 'flipYourCoins', label: 'Resets', color: getBehaviorColor(BehaviorType.FlipYourCoin), typeId: BehaviorType.FlipYourCoin },
    { key: 'makeItRights', label: 'Make It Rights', color: getBehaviorColor(BehaviorType.MakeItRight), typeId: BehaviorType.MakeItRight },
    { key: 'oneOnOneTimes', label: 'Let Go', color: getBehaviorColor(BehaviorType.OneOnOneTime), typeId: BehaviorType.OneOnOneTime },
  ] as const;

  // Helper function to get icon component by behavior type ID
  const getBehaviorIconByType = (behaviorType: number) => {
    return getBehaviorIcon(behaviorType as BehaviorType, { sx: { fontSize: 16, mr: 0.5 } });
  };

  // Process behavior records into hourly data aggregated across the selected time period
  const getHourlyData = (): HourlyData[] => {
    // If we have pre-processed hourly averages data, use that instead
    if (hourlyAveragesData) {
      return hourlyAveragesData.map(data => ({
        hour: formatHourLabel(data.hour),
        hourNum: data.hour,
        stars: Math.round(data.stars), // Round for display
        triangles: Math.round(data.triangles),
        squares: Math.round(data.squares),
        flipYourCoins: Math.round(data.flipYourCoins),
        makeItRights: Math.round(data.makeItRights),
        oneOnOneTimes: Math.round(data.oneOnOneTimes),
        total: Math.round(data.total)
      }));
    }

    // Original logic for processing individual behavior records
    const hourlyMap = new Map<number, { stars: number; triangles: number; squares: number; flipYourCoins: number; makeItRights: number; oneOnOneTimes: number }>();

    // Initialize all hours with zero counts
    for (let i = 0; i < 24; i++) {
      hourlyMap.set(i, { stars: 0, triangles: 0, squares: 0, flipYourCoins: 0, makeItRights: 0, oneOnOneTimes: 0 });
    }


    // Note: Empty behavior records is a normal state for new families

    // Aggregate ALL behaviors across the entire time period by hour of day
    // This gives us the total activity pattern for each hour across all days in the period
    behaviorRecords.forEach(record => {
      // Use eventTime first, then fall back to recordedAt for backward compatibility
      const timestamp = record.eventTime || record.recordedAt;
      const hour = getHourInFamilyTimezone(timestamp); // Extract hour (0-23) in family timezone
      const hourData = hourlyMap.get(hour);

      if (!hourData) {
        return;
      }

      switch (record.behaviorType) {
        case 1: // Star
          hourData.stars++;
          break;
        case 2: // Triangle
          hourData.triangles++;
          break;
        case 3: // Square
          hourData.squares++;
          break;
        case 4: // FlipYourCoin
          hourData.flipYourCoins++;
          break;
        case 5: // MakeItRight
          hourData.makeItRights++;
          break;
        case 6: // OneOnOneTime
          hourData.oneOnOneTimes++;
          break;
      }
    });

    // Convert to chart data format
    return Array.from(hourlyMap.entries()).map(([hourNum, data]) => ({
      hour: formatHourLabel(hourNum),
      hourNum,
      stars: data.stars,
      triangles: data.triangles,
      squares: data.squares,
      flipYourCoins: data.flipYourCoins,
      makeItRights: data.makeItRights,
      oneOnOneTimes: data.oneOnOneTimes,
      total: data.stars + data.triangles + data.squares + data.flipYourCoins + data.makeItRights + data.oneOnOneTimes,
    }));
  };

  const formatHourLabel = (hour: number): string => {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
  };

  // Day of week helper functions (Issue #1132)
  const formatDayLabel = (dayNum: number): string => DAY_NAMES[dayNum] || '';
  const formatDayAbbreviation = (dayNum: number): string => DAY_ABBREVIATIONS[dayNum] || '';

  // Process day-of-week data into chart format
  const getDayOfWeekData = (): DayOfWeekData[] => {
    if (!dayOfWeekAveragesData) {
      // Return empty data for all 7 days
      return Array.from({ length: 7 }, (_, i) => ({
        day: formatDayAbbreviation(i),
        dayNum: i,
        stars: 0,
        triangles: 0,
        squares: 0,
        flipYourCoins: 0,
        makeItRights: 0,
        oneOnOneTimes: 0,
        total: 0
      }));
    }

    return dayOfWeekAveragesData.map(data => ({
      day: formatDayAbbreviation(data.dayOfWeek),
      dayNum: data.dayOfWeek,
      stars: Math.round(data.stars),
      triangles: Math.round(data.triangles),
      squares: Math.round(data.squares),
      flipYourCoins: Math.round(data.flipYourCoins),
      makeItRights: Math.round(data.makeItRights),
      oneOnOneTimes: Math.round(data.oneOnOneTimes),
      total: Math.round(data.total)
    }));
  };

  const chartData: (HourlyData | DayOfWeekData)[] = viewMode === 'hour' ? getHourlyData() : getDayOfWeekData();

  // Check if all data is empty (no behaviors recorded)
  // Only show empty state if we have loaded data for the current view mode
  const hasDataForCurrentView = viewMode === 'hour' ? !!hourlyAveragesData : !!dayOfWeekAveragesData;
  const isEmpty = hasDataForCurrentView && chartData.every((item: HourlyData | DayOfWeekData) => item.total === 0);

  const handleBarClick = (data: { payload?: HourlyData }) => {
    if (!data || !data.payload) return;
    
    const hourData = data.payload as HourlyData;
    const hourBehaviors = behaviorRecords.filter(record => {
      const timestamp = record.eventTime || record.recordedAt;
      return getHourInFamilyTimezone(timestamp) === hourData.hourNum;
    });

    setSelectedHour({
      hour: hourData.hour,
      behaviors: hourBehaviors,
      stars: hourData.stars,
      triangles: hourData.triangles,
      squares: hourData.squares,
      flipYourCoins: hourData.flipYourCoins,
      makeItRights: hourData.makeItRights,
      oneOnOneTimes: hourData.oneOnOneTimes,
    });
  };

  const renderTooltip = ({ active, payload, label }: TooltipContentProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as HourlyData;
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: isNaluV1Theme ? '12px' : 1,
            padding: 2,
            boxShadow: isNaluV1Theme ? 'none' : 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {BEHAVIOR_CONFIG.map((behavior) => (
            <Box key={behavior.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {getBehaviorIcon(behavior.typeId, { sx: { fontSize: 16, color: behavior.color } })}
              <Typography variant="body2" sx={{ color: behavior.color }}>
                {behavior.label}: {data[behavior.key as keyof HourlyData]}
              </Typography>
            </Box>
          ))}
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            Total: {data.total}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const getPeriodTitle = () => {
    // Create base title with child-specific prefix if applicable and view mode (Issue #1132)
    let baseTitle = viewMode === 'hour' ? '⏰ Behavior Patterns by Time' : '⏰ Behavior Patterns by Day of Week';
    if (childName) {
      baseTitle = viewMode === 'hour'
        ? `⏰ ${childName}'s Behavior Patterns by Time`
        : `⏰ ${childName}'s Behavior Patterns by Day of Week`;
    }

    // Use unified time window if available, otherwise fall back to selectedPeriod
    if (timeWindow) {
      return getTimeWindowDisplayTitle(timeWindow, baseTitle);
    }

    // Fallback for deprecated selectedPeriod prop
    switch (selectedPeriod) {
      case 'week':
        return `${baseTitle} (Rolling 7 Days)`;
      case 'month':
        return `${baseTitle} (Rolling 30 Days)`;
      default:
        return baseTitle;
    }
  };

  // Heat map visualization component
  const renderHeatMap = () => {
    const maxTotal = Math.max(...chartData.map(d => d.total));
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: { xs: 0.25, sm: 0.5 }, 
        mt: 2,
        justifyContent: 'center'
      }}>
        {chartData.map((data, index) => {
          const intensity = maxTotal > 0 ? data.total / maxTotal : 0;
          const backgroundColor = `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, ${intensity * 0.8 + 0.1})`;

          // Get label based on view mode
          const label = viewMode === 'hour'
            ? (data as HourlyData).hour
            : formatDayLabel((data as DayOfWeekData).dayNum);

          return (
            <Box key={index} sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: { xs: '30px', sm: viewMode === 'dayOfWeek' ? '60px' : '40px' }
            }}>
              <Box
                onClick={() => {
                  if (data.total > 0) {
                    if (viewMode === 'hour') {
                      const hourData = data as HourlyData;
                      const hourBehaviors = behaviorRecords.filter(record => {
                        const timestamp = record.eventTime || record.recordedAt;
                        return getHourInFamilyTimezone(timestamp) === hourData.hourNum;
                      });
                      setSelectedHour({
                        hour: hourData.hour,
                        behaviors: hourBehaviors,
                        stars: hourData.stars,
                        triangles: hourData.triangles,
                        squares: hourData.squares,
                        flipYourCoins: hourData.flipYourCoins,
                        makeItRights: hourData.makeItRights,
                        oneOnOneTimes: hourData.oneOnOneTimes,
                      });
                    } else {
                      const dayData = data as DayOfWeekData;
                      // For day-of-week, filter behaviors by day of week
                      const dayBehaviors = behaviorRecords.filter(record => {
                        const timestamp = record.eventTime || record.recordedAt;
                        const date = new Date(timestamp);
                        return date.getDay() === dayData.dayNum;
                      });
                      setSelectedDay({
                        day: formatDayLabel(dayData.dayNum),
                        behaviors: dayBehaviors,
                        stars: dayData.stars,
                        triangles: dayData.triangles,
                        squares: dayData.squares,
                        flipYourCoins: dayData.flipYourCoins,
                        makeItRights: dayData.makeItRights,
                        oneOnOneTimes: dayData.oneOnOneTimes,
                      });
                    }
                  }
                }}
                sx={{
                  backgroundColor,
                  height: { xs: 30, sm: 40 },
                  width: { xs: 30, sm: viewMode === 'dayOfWeek' ? '60px' : '40px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: data.total > 0 ? 'pointer' : 'default',
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: isNaluV1Theme ? '12px' : 1,
                  transition: 'all 0.2s',
                  '&:hover': data.total > 0 ? {
                    backgroundColor: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, ${Math.min(intensity + 0.2, 0.9)})`,
                    transform: 'scale(1.05)',
                  } : {},
                }}
              >
                <Typography variant="caption" sx={{
                  fontSize: { xs: 6, sm: viewMode === 'dayOfWeek' ? 7 : 8 },
                  fontWeight: 600,
                  color: intensity > 0.5 ? 'white' : 'black'
                }}>
                  {label}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{
                display: 'block',
                textAlign: 'center',
                mt: 0.5,
                fontSize: { xs: 7, sm: 9 }
              }}>
                {data.total}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Box
        data-testid={testId}
        sx={{ width: '100%' }}
      >
        {/* Toggle between Day of Week and Hour of Day (Issue #1135) - Day of Week is default */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: viewMode === 'dayOfWeek' ? 600 : 'normal' }}>
            Day of Week
          </Typography>
          <Switch
            checked={viewMode === 'hour'}
            onChange={(e) => {
              const newMode = e.target.checked ? 'hour' : 'dayOfWeek';
              setViewMode(newMode);
            }}
            name="viewModeToggle"
          />
          <Typography variant="body2" sx={{ fontWeight: viewMode === 'hour' ? 600 : 'normal' }}>
            Hour of Day
          </Typography>
        </Box>

        {/* Show loading state */}
        {loading && (
          <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography color="text.secondary">Loading behavior patterns...</Typography>
            </Box>
          </Box>
        )}

        {/* Show error state */}
        {!loading && error && (
          <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="error" sx={{ textAlign: 'center', px: 3 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Show empty state if no behaviors recorded */}
        {!loading && !error && isEmpty && (
          <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary" sx={{ textAlign: 'center', px: 3 }}>
              No time pattern data available. Log behaviors throughout the day to see when they occur!
            </Typography>
          </Box>
        )}

        {/* Show chart when data is available */}
        {!loading && !error && !isEmpty && (
          <>

        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
          {getPeriodTitle()}
        </Typography>

        {/* Heat Map View */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center', color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Activity Heat Map - Total Behaviors by {viewMode === 'hour' ? 'Hour' : 'Day of Week'} {timeWindow ? getTimeWindowDisplayTitle(timeWindow, '') : (selectedPeriod === 'week' ? '(Past 7 Days)' : '(Past 30 Days)')}
          </Typography>
          {renderHeatMap()}
          <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            Darker colors indicate more total behavior activity across all days in the period
          </Typography>
        </Box>

        {/* Bar Chart View */}
        <Box sx={{ height: { xs: 250, sm: 300 }, width: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center', color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Detailed Breakdown
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={chartMargins}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={viewMode === 'hour' ? 'hour' : 'day'}
                fontSize={8}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis fontSize={10} />
              <Tooltip content={renderTooltip} />
              {BEHAVIOR_CONFIG.map((behavior) => (
                // Recharts primitive/render-prop internals — raw SVG, sx doesn't apply
                <Bar
                  key={behavior.key}
                  dataKey={behavior.key}
                  stackId="behaviors"
                  fill={behavior.color}
                  onClick={handleBarClick}
                  style={{ cursor: 'pointer' }}
                  data-testid={`time-bar-${behavior.key.toLowerCase()}`}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        
        {(
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
            Total activity patterns across {timeWindow ? getTimeWindowDisplayTitle(timeWindow, '').toLowerCase() : (selectedPeriod === 'week' ? '7 days' : '30 days')}
          </Typography>
        )}
          </>
        )}
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={Boolean(selectedHour)}
        onClose={() => setSelectedHour(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedHour?.hour} Details
        </DialogTitle>
        <DialogContent>
          {selectedHour && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {BEHAVIOR_CONFIG.map((behavior, index) => {
                  const count = selectedHour[behavior.key as keyof DetailedHourData] as number;
                  const labelLower = behavior.label.toLowerCase();
                  const pluralLabel = count === 1 ? labelLower.replace(/s$/, '') : labelLower;
                  return (
                    <span key={behavior.key}>
                      {index > 0 && ', '}
                      {count} {pluralLabel}
                    </span>
                  );
                })}
              </Typography>
              {selectedHour.behaviors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Behavior Details:
                  </Typography>
                  {selectedHour.behaviors.map((behavior, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {getBehaviorIconByType(behavior.behaviorType)}
                      <Typography variant="body2">
                        {behavior.notes}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Day of Week Detail Dialog (Issue #1132) */}
      <Dialog
        open={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          All {selectedDay?.day}s - Details
        </DialogTitle>
        <DialogContent>
          {selectedDay && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {BEHAVIOR_CONFIG.map((behavior, index) => {
                  const count = selectedDay[behavior.key as keyof DetailedDayData] as number;
                  const labelLower = behavior.label.toLowerCase();
                  const pluralLabel = count === 1 ? labelLower.replace(/s$/, '') : labelLower;
                  return (
                    <span key={behavior.key}>
                      {index > 0 && ', '}
                      {count} {pluralLabel}
                    </span>
                  );
                })}
              </Typography>
              {selectedDay.behaviors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Behavior Details:
                  </Typography>
                  {selectedDay.behaviors.map((behavior, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {getBehaviorIconByType(behavior.behaviorType)}
                      <Typography variant="body2">
                        {behavior.notes}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimePatternsChart;